// Data-access hooks for the Stores & Transfers screen-group (plan.md G24). Built on
// the two shared primitives from src/hooks/useApi.ts (useApiQuery/useApiMutation) per
// that file's own guidance ("module-specific hooks ... should be built from these two
// primitives"). Talks to three modules' endpoints:
//   - services/api/src/modules/stores/{issues,transfers}.ts — this goal's own domain.
//   - services/api/src/modules/config/{sites,cost-centres,locations,products}.ts —
//     read-only lookups for the guided-flow selects.
//   - services/api/src/modules/reporting/stock.ts — read-only, for the ledger-effect
//     preview's "estimated at current WAC" figure.
//
// NOTE on lookup-hook placement: sites/cost-centres/locations/products lookups are
// generic enough that G20/G21/G22/G23/G26 will each want the same shapes, but there is
// no shared "web lookups" file pre-created in plan.md's S0 registry, and this goal's
// ownership is scoped to stores-transfers.tsx (+sub-routes) and
// features/stores-transfers/ only — NOT src/hooks/. Duplicating a few small hooks
// here (rather than reaching into src/hooks/) keeps this goal's diff inside its own
// registry per the S0 rule; a future goal explicitly scoped to touch src/hooks/ could
// consolidate these across modules.
import { useQueryClient } from '@tanstack/react-query';
import type {
  StockAdjustmentPostResponse,
  StockAdjustmentReverseResponse,
  StockAdjustmentResponse,
  StockAdjustmentUpdate,
  StockAdjustmentWrite,
  StoresIssuePostResponse,
  StoresIssueReverseResponse,
  StoresIssueResponse,
  StoresIssueUpdate,
  StoresIssueWrite,
  TransferPostResponse,
  TransferReverseResponse,
  TransferResponse,
  TransferUpdate,
  TransferWrite,
} from '@zebra/core';
import { useApiMutation, useApiQuery } from '../../hooks/useApi';
import type { CostCentreRow, ListResponse, LocationRow, ProductRow, SiteRow, StockOnHandRow } from './types';

const LOOKUP_LIMIT = 200;

// ---------------------------------------------------------------------------
// Lookups (config + reporting modules — read-only from this feature's perspective)
// ---------------------------------------------------------------------------

export function useSites() {
  return useApiQuery<ListResponse<SiteRow>>(['config', 'sites'], '/config/sites', {
    query: { limit: LOOKUP_LIMIT },
  });
}

export function useCostCentres(siteId: string | undefined) {
  return useApiQuery<ListResponse<CostCentreRow>>(['config', 'cost-centres', siteId ?? null], '/config/cost-centres', {
    query: { siteId, limit: LOOKUP_LIMIT },
    enabled: Boolean(siteId),
  });
}

export function useLocations(siteId: string | undefined) {
  return useApiQuery<ListResponse<LocationRow>>(['config', 'locations', siteId ?? null], '/config/locations', {
    query: { siteId, isActive: true, limit: LOOKUP_LIMIT },
    enabled: Boolean(siteId),
  });
}

/** Every active location tenant-wide, unfiltered by site (`siteId` is optional on
 * `GET /config/locations` — see packages/core/src/schemas/config.ts's
 * `locationListQuerySchema`). Used by the transfer flow's location pickers: a
 * transfer's from/to SITE is derived from whichever location the user picks (each
 * `LocationRow` carries its own `siteId`), rather than asking the user to choose a
 * site first — that's what makes "same-site or cross-site" fall out naturally from
 * "pick a from-location, pick a to-location" (BUILD_SPEC.md §8.6). */
export function useAllLocations() {
  return useApiQuery<ListResponse<LocationRow>>(['config', 'locations', 'all'], '/config/locations', {
    query: { isActive: true, limit: LOOKUP_LIMIT },
  });
}

/** Typeahead product search (fuzzy-ish `q` substring match per config's
 * `listQueryBaseSchema` — see packages/core/src/schemas/config.ts). */
export function useProductSearch(search: string) {
  return useApiQuery<ListResponse<ProductRow>>(['config', 'products', search], '/config/products', {
    query: { q: search.trim() || undefined, isActive: true, limit: 50 },
  });
}

/** Single-record lookups, used to resolve a name/label for an id the caller already
 * has (e.g. re-opening a DRAFT whose lines reference products/locations outside the
 * current search page). */
export function useProduct(id: string | undefined) {
  return useApiQuery<ProductRow>(['config', 'products', 'byId', id ?? null], `/config/products/${id}`, {
    enabled: Boolean(id),
    retry: false,
  });
}

export function useLocation(id: string | undefined) {
  return useApiQuery<LocationRow>(['config', 'locations', 'byId', id ?? null], `/config/locations/${id}`, {
    enabled: Boolean(id),
    retry: false,
  });
}

export function useSite(id: string | undefined) {
  return useApiQuery<SiteRow>(['config', 'sites', 'byId', id ?? null], `/config/sites/${id}`, {
    enabled: Boolean(id),
    retry: false,
  });
}

export function useCostCentre(id: string | undefined) {
  return useApiQuery<CostCentreRow>(['config', 'cost-centres', 'byId', id ?? null], `/config/cost-centres/${id}`, {
    enabled: Boolean(id),
    retry: false,
  });
}

/** Every stock_levels row for a site, joined to its pool's derived WAC average
 * (`avgCostCentsPerUnit`) — fetched ONCE per site rather than once per line/product,
 * then looked up client-side per product for the ledger-effect preview. */
export function useStockOnHand(siteId: string | undefined) {
  return useApiQuery<{ items: StockOnHandRow[]; totalValueCents: string }>(
    ['reporting', 'stock-on-hand', siteId ?? null],
    '/reporting/stock-on-hand',
    { query: { siteId }, enabled: Boolean(siteId), retry: false },
  );
}

/** First-seen avg cost per product from a stock-on-hand page (the same pool average
 * repeats once per location row for a given product — see stock.ts). Products with no
 * stock_levels row yet at this site (never issued/received here) are simply absent —
 * callers should treat a missing entry as "no prior cost data at this site". */
export function avgCostByProduct(rows: StockOnHandRow[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of rows ?? []) {
    if (!map.has(row.productId)) map.set(row.productId, row.avgCostCentsPerUnit);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Stores issues
// ---------------------------------------------------------------------------

const issuesListKey = ['stores', 'issues', 'list'] as const;

export function useStoresIssuesList(cursor: string | undefined) {
  return useApiQuery<{ items: StoresIssueResponse[]; nextCursor: string | null }>(
    [...issuesListKey, cursor ?? null],
    '/stores/issues',
    { query: { cursor, limit: 20 } },
  );
}

export function useStoresIssue(id: string) {
  return useApiQuery<StoresIssueResponse>(['stores', 'issues', id], `/stores/issues/${id}`);
}

export function useCreateStoresIssue() {
  const queryClient = useQueryClient();
  return useApiMutation<StoresIssueResponse, StoresIssueWrite>('/stores/issues', 'POST', {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: issuesListKey }),
  });
}

export function useUpdateStoresIssue(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<StoresIssueResponse, StoresIssueUpdate>(`/stores/issues/${id}`, 'PATCH', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', 'issues', id] });
      queryClient.invalidateQueries({ queryKey: issuesListKey });
    },
  });
}

export function usePostStoresIssue(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<StoresIssuePostResponse, void>(`/stores/issues/${id}/post`, 'POST', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', 'issues', id] });
      queryClient.invalidateQueries({ queryKey: issuesListKey });
    },
  });
}

export function useReverseStoresIssue(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<StoresIssueReverseResponse, void>(`/stores/issues/${id}/reverse`, 'POST', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', 'issues', id] });
      queryClient.invalidateQueries({ queryKey: issuesListKey });
    },
  });
}

// ---------------------------------------------------------------------------
// Transfers
// ---------------------------------------------------------------------------

const transfersListKey = ['stores', 'transfers', 'list'] as const;

export function useTransfersList(cursor: string | undefined) {
  return useApiQuery<{ items: TransferResponse[]; nextCursor: string | null }>(
    [...transfersListKey, cursor ?? null],
    '/stores/transfers',
    { query: { cursor, limit: 20 } },
  );
}

export function useTransfer(id: string) {
  return useApiQuery<TransferResponse>(['stores', 'transfers', id], `/stores/transfers/${id}`);
}

export function useCreateTransfer() {
  const queryClient = useQueryClient();
  return useApiMutation<TransferResponse, TransferWrite>('/stores/transfers', 'POST', {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: transfersListKey }),
  });
}

export function useUpdateTransfer(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<TransferResponse, TransferUpdate>(`/stores/transfers/${id}`, 'PATCH', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', 'transfers', id] });
      queryClient.invalidateQueries({ queryKey: transfersListKey });
    },
  });
}

export function usePostTransfer(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<TransferPostResponse, void>(`/stores/transfers/${id}/post`, 'POST', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', 'transfers', id] });
      queryClient.invalidateQueries({ queryKey: transfersListKey });
    },
  });
}

export function useReverseTransfer(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<TransferReverseResponse, void>(`/stores/transfers/${id}/reverse`, 'POST', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', 'transfers', id] });
      queryClient.invalidateQueries({ queryKey: transfersListKey });
    },
  });
}

// ---------------------------------------------------------------------------
// Stock adjustments (G-A, Wave HOWICK-1)
// ---------------------------------------------------------------------------

const adjustmentsListKey = ['stores', 'adjustments', 'list'] as const;

export function useAdjustmentsList(cursor: string | undefined) {
  return useApiQuery<{ items: StockAdjustmentResponse[]; nextCursor: string | null }>(
    [...adjustmentsListKey, cursor ?? null],
    '/stores/adjustments',
    { query: { cursor, limit: 20 } },
  );
}

export function useAdjustment(id: string) {
  return useApiQuery<StockAdjustmentResponse>(['stores', 'adjustments', id], `/stores/adjustments/${id}`);
}

export function useCreateAdjustment() {
  const queryClient = useQueryClient();
  return useApiMutation<StockAdjustmentResponse, StockAdjustmentWrite>('/stores/adjustments', 'POST', {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adjustmentsListKey }),
  });
}

export function useUpdateAdjustment(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<StockAdjustmentResponse, StockAdjustmentUpdate>(`/stores/adjustments/${id}`, 'PATCH', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', 'adjustments', id] });
      queryClient.invalidateQueries({ queryKey: adjustmentsListKey });
    },
  });
}

export function usePostAdjustment(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<StockAdjustmentPostResponse, void>(`/stores/adjustments/${id}/post`, 'POST', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', 'adjustments', id] });
      queryClient.invalidateQueries({ queryKey: adjustmentsListKey });
    },
  });
}

export function useReverseAdjustment(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<StockAdjustmentReverseResponse, void>(`/stores/adjustments/${id}/reverse`, 'POST', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', 'adjustments', id] });
      queryClient.invalidateQueries({ queryKey: adjustmentsListKey });
    },
  });
}

export function useCancelAdjustment(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<StockAdjustmentResponse, void>(`/stores/adjustments/${id}/cancel`, 'POST', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', 'adjustments', id] });
      queryClient.invalidateQueries({ queryKey: adjustmentsListKey });
    },
  });
}
